import { useEffect } from "react";
import { useLocation, useNavigate, Location } from 'react-router-dom';
import { sunboundPayProxy } from '@proxies';
import currency from "currency.js";
import { Enums, helpers, Constants } from "@shared";
import { useSpinnerContext, useSessionContext } from '@contexts';
import { useFormSubmission, useAllowedPaymentTypes, type FormError } from "@hooks";
import { DateTime } from "luxon";
import { StatusCodes } from "http-status-codes";
import { Button } from "@common";
import { PaymentMethodBadge } from "./payment-method-badge";
import { TriangleAlert } from "lucide-react";
import type { CurrentBill } from '@app-types';
import { isApiError } from '@app-types';
import { PostHogEvents, PostHogPaymentFailedErrorType, capturePosthogEvent } from '@analytics';

interface LocationState {
    currentBill: CurrentBill;
    amount: number;
    account: {
        idPayor: number;
        idAccount: number;
    };
    idempotencyKey: string;
}

const ConfirmPayment = () => {

    const navigate = useNavigate(), location = useLocation() as Location<LocationState>;
    const { setSpinnerShow } = useSpinnerContext();
    const {
        allowedPaymentTypes,
        isLoading: allowedPaymentTypesIsLoading,
        isError: allowedPaymentTypesIsError
    } = useAllowedPaymentTypes();
    const { accounts } = useSessionContext();

    const {
        state: {
            currentBill,
            amount,
            account: {
                idPayor,
                idAccount
            },
            idempotencyKey
        } } = location;

    const account = accounts.find(acc => acc.idAccount === idAccount);
    const defaultPaymentMethod = account?.paymentPreferences?.defaultPaymentMethod;
    const paymentTypeFees = account?.accountPreferences?.paymentTypeFees;

    // To test a theoertical scenario where payment data is unavailable (to see
    // if the page can handle it gracefully), comment-out the 2 lines above and
    // un-comment the lines below.
    //
    // const defaultPaymentMethod = undefined;
    // const paymentTypeFees = undefined;

    const { dueDate, invitations } = currentBill;
    const month = helpers.monthName(dueDate);

    const hasPaymentMethodError = !defaultPaymentMethod;
    const hasPaymentFeesError = !paymentTypeFees;
    const hasAccountError = hasPaymentMethodError || hasPaymentFeesError;

    const hasDisallowedPaymentMethod =
        (!allowedPaymentTypesIsError &&
            !allowedPaymentTypesIsLoading &&
            defaultPaymentMethod?.idPaymentType !== undefined &&
            !allowedPaymentTypes.includes(defaultPaymentMethod.idPaymentType));

    const invite = invitations.find(i => i.idPayor === idPayor);

    const { setFormState, clearFormState, hasFormError, formError } = useFormSubmission<FormError>();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const addDefaultPM = () => {
        helpers.navigateTo(`/payment-method-selector`, location, navigate, { addDefaultPM: true });
    };

    const navigateToPaymentMethodSettings = () => {
        navigate(`/payment-method-settings`);
    };

    const navigateToDashboard = () => {
        navigate(`/`);
    };

    const handleAddDefaultPM = () => {
        addDefaultPM();
    };

    const handleKeyDown = (callback: () => void) => (e: React.KeyboardEvent) => {
        if (e.key === `Enter` || e.key === ` `) {
            e.preventDefault();
            callback();
        }
    };

    // Set initial error state if payment method is invalid or account data is missing
    useEffect(() => {
        if (hasAccountError) {
            setFormState({
                userMessage: (
                    <>
                        Unable to load payment information. Please ensure a <span role="button" tabIndex={0} onClick={navigateToPaymentMethodSettings} onKeyDown={handleKeyDown(navigateToPaymentMethodSettings)} className="underline font-medium focus-visible:outline-focus-ring cursor-pointer">default payment method</span> is set and return to your <span role="button" tabIndex={0} onClick={navigateToDashboard} onKeyDown={handleKeyDown(navigateToDashboard)} className="underline font-medium focus-visible:outline-focus-ring cursor-pointer">dashboard</span> to make a payment again.
                    </>
                ),
                prevented: true,
                handled: true
            });
        } else if (hasDisallowedPaymentMethod) {
            setFormState({
                userMessage: (
                    <>
                        Payment type invalid. Please <span role="button" tabIndex={0} onClick={handleAddDefaultPM} onKeyDown={handleKeyDown(handleAddDefaultPM)} className="underline font-medium focus-visible:outline-focus-ring cursor-pointer">use another payment method</span>.
                    </>
                ),
                prevented: true,
                handled: true
            });
        }
    }, [hasAccountError, hasDisallowedPaymentMethod]);

    const pay = async () => {
        if (hasAccountError || hasDisallowedPaymentMethod || allowedPaymentTypesIsLoading || allowedPaymentTypesIsError || !invite) {
            return;
        }
        setSpinnerShow(true);
        try {
            clearFormState();
            await sunboundPayProxy.pay(amount, invite.idInvitation, idempotencyKey);
            capturePosthogEvent(PostHogEvents.PAYMENT_CONFIRMED, {
                amount,
                total: total.value,
                paymentType: defaultPaymentMethod?.idPaymentType,
                fee: ptfee.value
            });
            helpers.navigateNext(location, navigate);
        } catch (error) {
            if (isApiError(error) && error.response?.status !== undefined && error.response.status >= StatusCodes.INTERNAL_SERVER_ERROR) {
                capturePosthogEvent(PostHogEvents.PAYMENT_FAILED, {
                    amount,
                    errorType: PostHogPaymentFailedErrorType.PLATFORM_ERROR,
                    statusCode: error.response.status
                });
                setFormState({ userMessage: Constants.Errors.PAYMENT_PLATFORM_ERROR, handled: false });
            } else if (isApiError(error)) {
                const { response: { data: { extendedError: { handled = false, userMessage = null } = {} } = {} } = {} } = error;
                capturePosthogEvent(PostHogEvents.PAYMENT_FAILED, {
                    amount,
                    errorType: PostHogPaymentFailedErrorType.API_ERROR,
                    handled
                });
                setFormState({ userMessage: userMessage || Constants.Errors.PAYMENT_PLATFORM_ERROR, handled });
            }

        } finally {
            setSpinnerShow(false);
        }
    };

    const handleCancel = () => {
        navigate(`/`);
    };

    const subTotal = currency(amount);

    let ptfee = currency(0);
    let total = subTotal;
    let feePercentage: string | null = null;
    let hasFeeData = false;

    if (paymentTypeFees && defaultPaymentMethod) {
        const ptFeeRule = paymentTypeFees.find(f => f.idPaymentType === defaultPaymentMethod.idPaymentType);
        if (ptFeeRule) {
            hasFeeData = true;
            if (ptFeeRule.idFeeType === Enums.FeeType.Fixed) {
                ptfee = currency(ptFeeRule.fee);
            } else {
                feePercentage = (ptFeeRule.fee * 100).toFixed(0);
                ptfee = currency(ptFeeRule.fee, { precision: 3 }).multiply(amount);
            }
            total = subTotal.add(ptfee);
        }
    }

    const isPaymentDisabled = hasAccountError || hasDisallowedPaymentMethod || allowedPaymentTypesIsLoading || allowedPaymentTypesIsError;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        pay();
    };

    return (
        <form method="POST" onSubmit={handleSubmit} className="flex flex-col w-full">
            {/* Error Display */}
            {hasFormError && (
                <>
                    <div className="w-full">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex flex-col gap-4">
                            <div className="flex items-center gap-2">
                                <TriangleAlert className="w-5 h-5 text-red-700" />
                                <h4 className="font-medium text-base leading-6 text-red-700 tracking-[0.16px]">{formError.prevented ? `Problem preventing payment` : `An error occurred`}</h4>
                            </div>
                            <p className="text-sm leading-5 text-neutral-800 tracking-[0.14px]">{formError.userMessage}</p>
                            {!formError.handled && (
                                <p className="text-xs leading-4 text-neutral-800 tracking-[0.12px]">
                                    If you have questions, please visit our <a className="text-blue-800 focus-visible:outline-focus-ring" href={Constants.Help.FAQ} target="_blank" rel="noreferrer">FAQ</a> or contact us at <a className="text-blue-800 focus-visible:outline-focus-ring" href={`mailto:${Constants.Help.Email}?subject=Payment support`}>{Constants.Help.Email}</a>
                                </p>
                            )}
                        </div>
                    </div>
                    {/* Spacing */}
                    <div className="h-8" />
                </>
            )}

            {/* Header */}
            <div className="border-b border-neutral-100 pb-6 w-full">
                <h2 className="font-medium text-[20px] leading-6 md:text-[28px] md:leading-[40px] text-neutral-950 tracking-[0.2px] md:tracking-[0.28px]">
                    Payment
                </h2>
            </div>

            {/* Spacing after header */}
            <div className="h-8" />

            {/* Content */}
            <div className="flex flex-col items-start w-full mx-auto">
                {/* Title and Authorization Text */}
                <div className="flex flex-col gap-2 w-full">
                    <h3 className="font-medium text-[20px] leading-6 text-neutral-950 tracking-[0.2px]">
                        Confirm Payment for {month} Balance
                    </h3>
                    <p className="text-sm leading-5 text-neutral-700 tracking-[0.14px]">
                        By clicking below you authorize Sunbound to charge your account for the amount specified below.
                    </p>
                </div>

                {/* Spacing */}
                <div className="h-4" />

                {/* Payment Receipt */}
                <div className="bg-white border-t border-l border-r border-neutral-200 rounded-t-lg w-full relative">
                    <div className="p-6 flex flex-col gap-4" role="list">
                        {/* Amount */}
                        <div className="flex items-center gap-2" role="listitem">
                            <span className="text-sm leading-5 text-neutral-700 tracking-[0.14px]" role="term">Amount</span>
                            <div className="flex-1 h-4 border-b-2 border-dotted border-neutral-200" aria-hidden="true" />
                            <span className="text-sm leading-5 text-neutral-950 tracking-[0.14px]" role="definition">{subTotal.format()}</span>
                        </div>

                        {/* Processing Fee */}
                        <div className="flex items-center gap-2" role="listitem">
                            <span className="text-sm leading-5 text-neutral-700 tracking-[0.14px]" role="term">
                                {hasFeeData && feePercentage ? `${feePercentage}% Processing Fee` : `Processing Fee`}
                            </span>
                            <div className="flex-1 h-4 border-b-2 border-dotted border-neutral-200" aria-hidden="true" />
                            <span className="text-sm leading-5 text-neutral-950 tracking-[0.14px]" role="definition">
                                {hasFeeData ? currency(ptfee).format() : `Not loaded`}
                            </span>
                        </div>

                        {/* Payment Method */}
                        <div className="flex items-center gap-2" role="listitem">
                            <span className="text-sm leading-5 text-neutral-700 tracking-[0.14px]" role="term">Payment Method</span>
                            <div className="flex-1 h-4 border-b-2 border-dotted border-neutral-200" aria-hidden="true" />
                            <div className="flex items-center gap-1" role="definition">
                                <PaymentMethodBadge
                                    paymentType={defaultPaymentMethod?.idPaymentType}
                                    sourceName={defaultPaymentMethod?.sourceName}
                                />
                                <span className="text-sm leading-5 text-neutral-950 tracking-[0.14px]">
                                    {defaultPaymentMethod?.last4 || `Not set`}
                                </span>
                            </div>
                        </div>

                        {/* Payment Date */}
                        <div className="flex items-center gap-2" role="listitem">
                            <span className="text-sm leading-5 text-neutral-700 tracking-[0.14px]" role="term">Payment Date</span>
                            <div className="flex-1 h-4 border-b-2 border-dotted border-neutral-200" aria-hidden="true" />
                            <span className="text-sm leading-5 text-neutral-950 tracking-[0.14px]" role="definition">{DateTime.now().toLocaleString({ year: `numeric`, month: `2-digit`, day: `2-digit` })}</span>
                        </div>

                        {/* Total */}
                        <div className="flex items-center gap-2" role="listitem">
                            <span className="text-base leading-6 text-neutral-950 tracking-[0.16px]" role="term">Total</span>
                            <div className="flex-1 h-4 border-b-2 border-dotted border-neutral-200" aria-hidden="true" />
                            <span className="text-base leading-6 text-neutral-950 tracking-[0.16px]" role="definition">{total.format()}</span>
                        </div>
                    </div>
                    {/* Torn paper effect */}
                    <div
                        className="absolute left-0 h-[9px] w-full bg-white"
                        style={{
                            bottom: `-5px`, // not perfect
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='21' height='9' viewBox='0 0 21 9' fill='none'%3E%3Cpath d='M10.3988 8.5L10.0881 8.89177C10.2701 9.03608 10.5275 9.03608 10.7095 8.89177L10.3988 8.5ZM20.7977 0.891768C21.0141 0.720188 21.0504 0.405694 20.8788 0.189327C20.7072 -0.0270407 20.3927 -0.0633481 20.1763 0.108232L20.487 0.5L20.7977 0.891768ZM0.310547 0.5L-0.000126302 0.891768L10.0881 8.89177L10.3988 8.5L10.7095 8.10823L0.62122 0.108232L0.310547 0.5ZM10.3988 8.5L10.7095 8.89177L20.7977 0.891768L20.487 0.5L20.1763 0.108232L10.0881 8.10823L10.3988 8.5Z' fill='%23D3DBE4'/%3E%3C/svg%3E")`,
                            backgroundRepeat: `repeat-x`,
                            backgroundPosition: `bottom`,
                            backgroundSize: `21px 9px`
                        }}
                    />
                </div>

                {/* Spacing */}
                <div className="h-8" />

                {/* Buttons */}
                <div className="flex gap-4 w-full">
                    <Button variant="secondary" size="lg" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        disabled={isPaymentDisabled}
                        className="flex-1"
                    >
                        Confirm Payment
                    </Button>
                </div>
            </div>

            {/* Flexible spacing before footer - pushes footer to bottom */}
            <div className="flex-1 min-h-20" />
        </form>
    );
};

export default ConfirmPayment;
